import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo } from "react"
import { z } from "zod"

import {
  appNotificationSchema,
  type AppNotification,
} from "@/core/notifications/schemas.js"
import { useSound } from "@/hooks/use-sound"

import { getCsrfTokenFromDocumentCookie } from "./auth-api"
import { notificationPopSound } from "./notification-pop"
import { notificationsKeys } from "./query-keys"
import {
  notificationInboxStore,
  useNotificationInboxCursor,
  useNotificationInboxPageSize,
} from "./stores/notification-inbox-ui-store"

export type NotificationsInboxResponse = {
  items: AppNotification[]
  nextCursor: string | null
}

const notificationCreatedEventSchema = z.object({
  version: z.literal(1),
  type: z.literal("notification.created"),
  item: appNotificationSchema,
})

const readErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const body = (await response.json()) as {
      error?: { message?: string }
      message?: string
    }

    return body.error?.message ?? body.message ?? fallback
  } catch {
    return fallback
  }
}

const getCsrfToken = (): string => {
  const token = getCsrfTokenFromDocumentCookie()
  if (!token) {
    throw new Error("Missing CSRF token")
  }

  return token
}

export const notificationsApi = {
  listInbox: async (input?: {
    cursor?: string | null
    limit?: number
  }): Promise<NotificationsInboxResponse> => {
    const query = new URLSearchParams()
    if (input?.cursor) {
      query.set("cursor", input.cursor)
    }
    if (typeof input?.limit === "number") {
      query.set("limit", String(input.limit))
    }

    const response = await fetch(
      `/api/notifications${query.size > 0 ? `?${query.toString()}` : ""}`,
      {
        method: "GET",
        credentials: "include",
      },
    )

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to load notifications"),
      )
    }

    return (await response.json()) as NotificationsInboxResponse
  },
  markRead: async (notificationId: string): Promise<AppNotification> => {
    const response = await fetch(`/api/notifications/${notificationId}/read`, {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to mark notification as read"),
      )
    }

    const payload = (await response.json()) as { item: AppNotification }
    return payload.item
  },
  markAllRead: async (): Promise<number> => {
    const response = await fetch("/api/notifications/read-all", {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          "Failed to mark all notifications read",
        ),
      )
    }

    const payload = (await response.json()) as { updated: number }
    return payload.updated
  },
  deleteOne: async (notificationId: string): Promise<void> => {
    const response = await fetch(`/api/notifications/${notificationId}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to delete notification"),
      )
    }
  },
  deleteRead: async (): Promise<number> => {
    const response = await fetch("/api/notifications/delete-read", {
      method: "POST",
      credentials: "include",
      headers: {
        "x-csrf-token": getCsrfToken(),
      },
    })

    if (!response.ok) {
      throw new Error(
        await readErrorMessage(response, "Failed to delete read notifications"),
      )
    }

    const payload = (await response.json()) as { deleted: number }
    return payload.deleted
  },
}

function invalidateNotificationQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  return queryClient.invalidateQueries({
    queryKey: notificationsKeys.inboxBase(),
  })
}

type NotificationsRealtimeState = {
  refCount: number
  socket: WebSocket | null
  reconnectTimeout: ReturnType<typeof setTimeout> | null
  controller: AbortController | null
  queryClient: ReturnType<typeof useQueryClient> | null
  playNotificationPopup: (() => void) | null
}

const notificationsRealtimeState: NotificationsRealtimeState = {
  refCount: 0,
  socket: null,
  reconnectTimeout: null,
  controller: null,
  queryClient: null,
  playNotificationPopup: null,
}

const clearNotificationsReconnectTimeout = () => {
  if (!notificationsRealtimeState.reconnectTimeout) {
    return
  }

  clearTimeout(notificationsRealtimeState.reconnectTimeout)
  notificationsRealtimeState.reconnectTimeout = null
}

const stopNotificationsRealtime = () => {
  clearNotificationsReconnectTimeout()
  notificationsRealtimeState.controller?.abort()
  notificationsRealtimeState.controller = null
  notificationsRealtimeState.socket?.close()
  notificationsRealtimeState.socket = null
}

const refetchNotificationsCoreQueries = () => {
  const queryClient = notificationsRealtimeState.queryClient
  if (!queryClient) {
    return
  }

  void invalidateNotificationQueries(queryClient)
}

const connectNotificationsRealtime = () => {
  if (
    typeof window === "undefined" ||
    !notificationsRealtimeState.controller ||
    notificationsRealtimeState.refCount <= 0
  ) {
    return
  }

  clearNotificationsReconnectTimeout()

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  const csrfToken = getCsrfTokenFromDocumentCookie()
  const wsUrl = new URL(
    `${protocol}//${window.location.host}/api/notifications/ws`,
  )
  if (csrfToken) {
    wsUrl.searchParams.set("csrf_token", csrfToken)
  }
  const socket = new WebSocket(wsUrl.toString())
  notificationsRealtimeState.socket = socket

  socket.onopen = () => {
    refetchNotificationsCoreQueries()
  }

  socket.onmessage = (event) => {
    if (typeof event.data !== "string") {
      return
    }

    let raw: unknown
    try {
      raw = JSON.parse(event.data)
    } catch {
      return
    }

    const parsed = notificationCreatedEventSchema.safeParse(raw)
    if (!parsed.success) {
      return
    }

    const queryClient = notificationsRealtimeState.queryClient
    if (!queryClient) {
      return
    }

    const item = parsed.data.item
    const { pageSize } = notificationInboxStore.getState()
    const firstPageQueryKey = notificationsKeys.inbox({
      cursor: null,
      limit: pageSize,
    })

    queryClient.setQueryData<NotificationsInboxResponse>(
      firstPageQueryKey,
      (previous) => {
        if (!previous) {
          return {
            items: [item],
            nextCursor: null,
          }
        }

        if (previous.items.some((existing) => existing.id === item.id)) {
          return previous
        }

        const items = [item, ...previous.items].slice(
          0,
          notificationInboxStore.getState().pageSize,
        )
        return {
          ...previous,
          items,
        }
      },
    )

    notificationsRealtimeState.playNotificationPopup?.()
  }

  socket.onclose = () => {
    if (notificationsRealtimeState.refCount <= 0) {
      return
    }

    notificationsRealtimeState.reconnectTimeout = setTimeout(() => {
      connectNotificationsRealtime()
    }, 2_000)
  }
}

const ensureNotificationsRealtimeStarted = () => {
  if (notificationsRealtimeState.controller) {
    return
  }

  notificationsRealtimeState.controller = new AbortController()
  connectNotificationsRealtime()

  if (typeof document !== "undefined") {
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "visible") {
          refetchNotificationsCoreQueries()
        }
      },
      { signal: notificationsRealtimeState.controller.signal },
    )
  }

  if (typeof window !== "undefined") {
    window.addEventListener(
      "online",
      () => {
        refetchNotificationsCoreQueries()
        if (
          !notificationsRealtimeState.socket ||
          notificationsRealtimeState.socket.readyState === WebSocket.CLOSED
        ) {
          connectNotificationsRealtime()
        }
      },
      { signal: notificationsRealtimeState.controller.signal },
    )
  }
}

export const useNotifications = () => {
  const queryClient = useQueryClient()
  const cursor = useNotificationInboxCursor()
  const pageSize = useNotificationInboxPageSize()

  const inboxQuery = useQuery({
    queryKey: notificationsKeys.inbox({ cursor, limit: pageSize }),
    queryFn: () => notificationsApi.listInbox({ cursor, limit: pageSize }),
  })

  const onSuccess = () => invalidateNotificationQueries(queryClient)

  const markReadMutation = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess,
  })

  const markAllReadMutation = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess,
  })

  const deleteNotificationMutation = useMutation({
    mutationFn: notificationsApi.deleteOne,
    onSuccess,
  })

  const deleteReadNotificationsMutation = useMutation({
    mutationFn: notificationsApi.deleteRead,
    onSuccess,
  })

  const [playNotificationPopup] = useSound(notificationPopSound)

  useEffect(() => {
    notificationsRealtimeState.refCount += 1
    notificationsRealtimeState.queryClient = queryClient
    notificationsRealtimeState.playNotificationPopup = playNotificationPopup
    ensureNotificationsRealtimeStarted()

    return () => {
      notificationsRealtimeState.refCount = Math.max(
        notificationsRealtimeState.refCount - 1,
        0,
      )
      if (notificationsRealtimeState.refCount === 0) {
        stopNotificationsRealtime()
      }
    }
  }, [queryClient, playNotificationPopup])

  return {
    inboxQuery,
    markReadMutation,
    markAllReadMutation,
    deleteNotificationMutation,
    deleteReadNotificationsMutation,
  }
}

// Unread count is derived from the first page of the inbox.
// NOTE: The inbox is paginated, so this only reflects unreads within the first
// page. If the user has more unread notifications than fit on a single page,
// this count will undercount. A dedicated server-side unread-count endpoint can
// be re-introduced later if exact counts are needed.
export function useUnreadNotificationCount(): number {
  const pageSize = useNotificationInboxPageSize()
  const { data } = useQuery({
    queryKey: notificationsKeys.inbox({ cursor: null, limit: pageSize }),
    queryFn: () =>
      notificationsApi.listInbox({ cursor: null, limit: pageSize }),
  })

  return useMemo(
    () => (data?.items ?? []).filter((item) => item.readAt === null).length,
    [data?.items],
  )
}
